import { Component, Input, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { CurrentOrderService } from 'src/app/services/manager/current-order.service';
import { SubscriberService } from 'src/app/services/manager/subscriber.service';
import { ImageCacheService } from 'src/app/services/data/image-cache.service';
import { StorageService } from 'src/app/services/data/storage.service';
import { Subject } from 'rxjs';

const COLOR_MAP: Record<string, string> = {
  'bianco': '#f2f2f2', 'gesso': '#ede8de', 'margarina': '#f0ead2', 'naturale': '#e8e0cc',
  'panna': '#f0e6d0', 'avorio': '#e8ddb5', 'burro': '#f5e6c0', 'perla': '#e8e2d8',
  'nude': '#e0c8b0', 'ghiaccio': '#ddeef5', 'latte': '#d8c4a0',
  'grigio': '#9e9e9e', 'grigio chiaro': '#d0d0d0', 'grigiochiaro': '#d0d0d0',
  'grigio medio': '#8a8a8a', 'grigio medio melange': '#8a8a8a', 'grigiomediomelange': '#8a8a8a',
  'grigio scuro': '#555', 'grigio melange': '#a0a0a0',
  'antracite': '#455060', 'graphite': '#505a60', 'piombo': '#6a7480',
  'lavagna': '#5a6068', 'cemento': '#909090', 'lavaggio scuro': '#606878',
  'nero': '#1a1a1a', 'moro': '#141414',
  'beige': '#d8c8a8', 'sabbia': '#c8b48a', 'corda': '#b09060', 'corda melange': '#b89870',
  'tabacco': '#8b6340', 'nocciola': '#9c6b3c', 'cammello': '#c09850',
  'cioccolato': '#6b3a1f', 'fango': '#8c7655', 'taupe': '#8b7d72', 'tortora': '#9e8c7e',
  'mastice': '#b0a890', 'coccio': '#a05c48',
  'rosso': '#c0392b', 'amarone': '#8b1a2a', 'amaranto': '#8b1a40',
  'bordeaux': '#6b1020', 'burgundy': '#6b1020', 'vino': '#6b1428', 'mosto': '#5a1030',
  'prugna': '#7a1848', 'melanzana': '#6a1545', 'ruggine': '#b04010',
  'arancione': '#e07820', 'senape': '#b88c10', 'kaky': '#8b8640', 'giallo': '#f0c020',
  'ocra': '#c88c20', 'oro': '#d4a010',
  'verde': '#3a8040', 'verde militare': '#4a5230', 'militare': '#4a5230',
  'salvia': '#8a9a58', 'bottiglia': '#1a4a30', 'kaki': '#8b864e',
  'menta': '#80c8a8', 'acqua': '#00bcd4',
  'blu': '#1a3a7a', 'navy': '#0d1b4b', 'denim': '#2a5080', 'jeans': '#2a5080',
  'avio': '#4a6a90', 'bluette': '#5a80b0', 'azzurro': '#5090c0', 'celeste': '#80b8e0',
  'indaco': '#3040a0', 'cobalto': '#0040a0', 'ottanio': '#1a7070',
  'viola': '#6040a0', 'lavanda': '#9090c0', 'lilla': '#b080c0',
  'malva': '#b07090', 'ametista': '#8040a0',
  'rosa': '#e080a0', 'cipria': '#e0a090', 'silver pink': '#c8a0a8',
  'fucsia': '#d01880', 'salmone': '#e08070', 'corallo': '#e06858',
  'verde-militare': '#4a5230', 'latteblucoccio': '#8a7a60',
  'masticelatte': '#c0b090', 'militare-latte': '#7a7a58',
  'lav-blu-medio': '#7080a8', 'piombo/nero': '#404850',
};

function colorHexFallback(prod: any): string {
  if (prod.colorHex) return prod.colorHex;
  const norm = (s: string) => (s || '').toLowerCase().trim().replace(/\s+/g, ' ');
  const slug = norm(prod.color || '');
  if (COLOR_MAP[slug]) return COLOR_MAP[slug];
  const nameKey = norm(prod.colorname || '');
  if (COLOR_MAP[nameKey]) return COLOR_MAP[nameKey];
  const firstWord = nameKey.split(/[\s/]/)[0];
  if (firstWord && COLOR_MAP[firstWord]) return COLOR_MAP[firstWord];
  for (const part of slug.split(/[-/]/)) {
    if (COLOR_MAP[part]) return COLOR_MAP[part];
  }
  return '#888';
}

@Component({
  selector: 'app-product-details',
  templateUrl: './product-details.component.html',
  styleUrls: ['./product-details.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductDetailsComponent implements OnInit, OnDestroy {
  @Input() product: any[] = [];
  expandedSections: Set<string> = new Set();
  lastOpenedId: string | null = null;
  currentOrderProducts: any[] = [];
  currentOrderId: string | null = null;
  showTranslations = false;
  private destroy$ = new Subject<null>();

  constructor(
    private modalController: ModalController,
    private currentOrderService: CurrentOrderService,
    private sub: SubscriberService,
    private imageCache: ImageCacheService,
    private storage: StorageService,
    private cdr: ChangeDetectorRef
  ) {}

  colorHex(prod: any): string { return colorHexFallback(prod); }
  proxyUrl(url: string): string { return this.imageCache.proxyUrl(url); }
  totalQty(prod: any): number { return (prod.sizes ?? []).reduce((sum: number, s: any) => sum + (Number(s.quantity) || 0), 0); }
  sizesWithQty(prod: any): any[] { return (prod.sizes ?? []).filter((s: any) => (Number(s.quantity) || 0) > 0); }

  ngOnDestroy() {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  async ngOnInit() {
    this.showTranslations = (await this.storage.getData<boolean>('showTranslations')) === true;
    const autoExpand = (await this.storage.getData<boolean>('autoExpandSizesWithQty')) === true;

    if (this.product) {
      // Abrufen des aktuellen Zustands der Bestellung
      const currentOrder = this.sub.currentOrder.getValue(); // Abruf der `currentOrder`-Daten
      this.currentOrderProducts = currentOrder?.products || [];
      this.currentOrderId = currentOrder?.orderId ?? null;

      // Synchronisiere Produkte und Größen
      this.product.forEach((prod: any) => {
        const existingProduct = this.currentOrderProducts.find(
          (p) => p.productId === prod.productId
        );

        if (existingProduct) {
          // Synchronisiere Größenmengen
          prod.sizes = this.mergeSizes(prod.sizes, existingProduct.sizes);

          // Nur wenn in den Einstellungen aktiviert (Default AUS): automatisch ausklappen
          if (autoExpand && prod.sizes.some((size: any) => size.quantity > 0)) {
            this.expandedSections.add(prod.productId);
          }
        }
      });
    }

    // ngOnInit ist async — bei OnPush wird nach Abschluss des awaits ohne
    // markForCheck() nicht automatisch neu gerendert (z.B. der "Vorhanden"-Chip
    // bliebe bis zum ersten Klick unsichtbar).
    this.cdr.markForCheck();
  }

  // Größen zusammenführen
  private mergeSizes(newSizes: any[], existingSizes: any[]): any[] {
    const sizeMap = new Map();

    // Bestehende Größen hinzufügen
    existingSizes.forEach((size) => {
      sizeMap.set(size.size, { ...size });
    });

    // Neue Größen hinzufügen oder überschreiben
    newSizes.forEach((size) => {
      const existing = sizeMap.get(size.size);
      sizeMap.set(size.size, {
        ...size,
        quantity: existing?.quantity ?? size.quantity ?? 0, // Bevorzugt bestehende Menge
      });
    });

    return Array.from(sizeMap.values());
  }

  // Vom Karma-Unavailable-Badge automatisch ergänzter Info-Text
  onKarmaInfoUpdated(prod: any, newInfo: string) {
    prod.info = newInfo;
    this.cdr.markForCheck();
  }

  // Akkordeon umschalten — auch Sektionen mit Mengen können geschlossen werden
  toggleSection(productId: string) {
    if (this.expandedSections.has(productId)) {
      this.expandedSections.delete(productId);
      if (this.lastOpenedId === productId) {
        this.lastOpenedId = null;
      }
    } else {
      for (const p of this.product) {
        if (p.productId !== productId && this.expandedSections.has(p.productId) && !p.sizes?.some((s: any) => s.quantity > 0)) {
          this.expandedSections.delete(p.productId);
        }
      }
      this.expandedSections.add(productId);
      this.lastOpenedId = productId;
    }
  }

  // Größenmengen aktualisieren
  updateQuantity(size: any, quantity: any) {
    const parsedQuantity = parseInt(quantity, 10);
    size.quantity = isNaN(parsedQuantity) ? 0 : parsedQuantity;
    this.cdr.markForCheck();
  }

  incrementQuantity(size: any) {
    const current = Number(size?.quantity ?? 0);
    size.quantity = current + 1;
    this.cdr.markForCheck();
  }

  decrementQuantity(size: any) {
    const current = Number(size?.quantity ?? 0);
    size.quantity = Math.max(0, current - 1);
    this.cdr.markForCheck();
  }

  // TrackBy Funktionen für bessere Performance
  trackByModelId(index: number, item: any): string {
    return item.productId || index.toString();
  }

  trackBySize(index: number, size: any): string {
    return size.size || index.toString();
  }

  // Produkte speichern
  save() {
    this.currentOrderService.addOrUpdateProducts(this.product);
    this.modalController.dismiss();
  }

  // Modal schließen
  close() {
    this.modalController.dismiss();
  }
}
